// Eligibility state derived app-side: the apiv2 get_patient_file view keys `checkedAt` off
// care_plans.context.claims_updated_at, which nothing writes — so it read null for everyone.
// Source here: the check_eligibility cms_submission (verdict + when), else care_period.status.
import { CmsResultCodes } from './cmsResultCodes';
import type { CarePeriodStatus } from './carePeriod';

export type EligibilityState = 'not_checked' | 'eligible' | 'ineligible' | 'in_review';

export interface EligibilitySubmissionLike {
  result_code?: string | null;
  result_display?: string | null;
  created?: string | null;
  created_at?: string | null;
  finished_at?: string | null;
}

export interface EligibilityView {
  state: EligibilityState;
  checkedAt: string | null;
  resultCode: string | null;
  resultDisplay: string | null;
  /** true only when eligibility was never determined — the one case the modal warns on. */
  needsCheck: boolean;
}

// Exhaustive per-status map — adding a CarePeriodStatus without a mapping is a compile error
// here, forcing a decision (keyed off the canonical enum in logic/carePeriod.ts).
const STATUS_STATE: Record<CarePeriodStatus, EligibilityState> = {
  pending: 'not_checked',
  pending_eligibility: 'not_checked',
  pending_alignment: 'eligible',
  ineligible: 'ineligible',
  active: 'eligible',
  pending_unalignment: 'eligible',
  unaligned: 'eligible',
  completed: 'eligible',
  withdrawn: 'eligible',
};

function stateFromStatus(status: string | null | undefined): EligibilityState {
  const s = (status == null ? '' : String(status)).trim().toLowerCase();
  return STATUS_STATE[s as CarePeriodStatus] ?? 'not_checked'; // unknown/blank → not checked
}

function stateFromCode(code: string): EligibilityState {
  if (CmsResultCodes.isSuccess(code)) return 'eligible';
  if (CmsResultCodes.isDeadEnd(code)) return 'ineligible';
  return 'in_review'; // present but non-terminal (e.g. a fixable input error)
}

/** Prefer the check_eligibility submission (verdict + when); fall back to care_period.status. */
export function deriveEligibility(
  carePeriodStatus: string | null | undefined,
  submission?: EligibilitySubmissionLike | null,
): EligibilityView {
  const code = submission?.result_code ?? null;
  if (submission && code && String(code).trim()) {
    return {
      state: stateFromCode(code),
      checkedAt: submission.finished_at || submission.created || submission.created_at || null,
      resultCode: code,
      resultDisplay: submission.result_display ?? null,
      needsCheck: false,
    };
  }
  const state = stateFromStatus(carePeriodStatus);
  return {
    state,
    checkedAt: null,
    resultCode: null,
    resultDisplay: null,
    needsCheck: state === 'not_checked',
  };
}

/* ── Alignment-task outcome of an eligibility check ──────────────────────────
 * Since 2026-09-11 `onb_cms_alignment` is gated on the baseline labs review having
 * resolved, so an ELIGIBLE verdict routinely creates no task. WF12 reports which happened
 * on `side_effects.alignment_task`; these are the operator-facing readings of that value.
 *
 * Why this has to be rendered at all: the eligibility modal is the one screen where a
 * coordinator learns the verdict. Before the gate it always said "Context attached to CMS
 * alignment submission"; if the deferral said nothing, the coordinator would go looking for
 * a task that will not exist for ~3 weeks. Spec:
 * docs/specs/2026-09-11-alignment-task-gated-on-labs-review.md
 */
export type AlignmentTaskOutcome =
  | 'created' | 'deferred_labs_review' | 'gate_read_failed'
  | 'skipped' | 'no_template' | 'error';

const ALIGNMENT_TASK_NOTES: Partial<Record<AlignmentTaskOutcome, string>> = {
  created: 'CMS alignment task created',
  deferred_labs_review: 'CMS alignment task opens once the baseline lab review is closed out',
  gate_read_failed:
    'Could not check the baseline lab review — the CMS alignment task will be created automatically once it can be',
};

/** Operator-facing line for WF12's alignment-task outcome, or null when there is nothing
 *  worth saying (no care period, no template, or a plain write error the panel reports
 *  elsewhere). Unknown values return null rather than leaking a raw enum to the screen. */
export function alignmentTaskNote(outcome: string | null | undefined): string | null {
  if (!outcome) return null;
  return ALIGNMENT_TASK_NOTES[outcome as AlignmentTaskOutcome] ?? null;
}
